import { useState } from "react";
import {
  API, G, BORDER, BLACK, MID, LIGHT,
  DEMO_PRODUCTS, SHOPIFY_BRAND_DATA,
  Shell,
  S01, S02, S03, S04, S05, S06, S07,
} from "./sift";
import { useShopify, useGoogle, useBrandConfig, useCopyGeneration, useInitialLoad } from "./sift/hooks";

export default function App({ initialMode = "full" }) {
  const [step, setStep] = useState(0);
  const [sel, setSel]   = useState([]);
  const [fixes, setFixes] = useState({});
  const [appr, setAppr] = useState([]);
  const [demoMode, setDemoMode] = useState(false);
  const [appMode, setAppMode] = useState(initialMode); // "lite" or "full"

  const shop = useShopify();
  const google = useGoogle();
  const brand = useBrandConfig();

  const activeProducts = (shop.shopConnected && shop.shopProducts.length > 0 && !demoMode)
    ? shop.shopProducts
    : DEMO_PRODUCTS;

  const copy = useCopyGeneration({
    activeProducts, shopDomain: shop.shopDomain, shopConnected: shop.shopConnected,
    demoMode, appr, fixes, tone: brand.tone, words: brand.words, voice: brand.voice,
  });

  const handleStartDemo = (mode) => {
    setDemoMode(true);
    if (mode) setAppMode(mode);
    brand.applyDemo();
  };

  const handleExitDemo = () => {
    setDemoMode(false);
    setStep(0);
    setSel([]); setFixes({}); setAppr([]);
    brand.reset();
  };

  const reset = () => {
    setStep(0); setSel([]); setFixes({}); setAppr([]);
    copy.resetCopy();
  };

  useInitialLoad({
    fetchShopInfo: shop.fetchShopInfo,
    fetchShopProducts: shop.fetchShopProducts,
    loadBrandConfig: brand.load,
    setShopLoading: shop.setShopLoading,
    markGoogleConnected: google.markConnected,
    startDemo: handleStartDemo,
  });

  const isLoading = copy.isGenerating || copy.isPushing;
  const isLite = appMode === "lite";

  return (
    <Shell step={step} setStep={setStep} demoMode={demoMode} onExitDemo={handleExitDemo} appMode={appMode} setAppMode={setAppMode}>

      {/* AI Generation loading screen */}
      {copy.isGenerating && (
        <div data-testid="ai-generating-screen" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400, gap: 20, animation: "pop 0.3s ease" }}>
          <div style={{ width: 48, height: 48, border: `3px solid ${BORDER}`, borderTopColor: G, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: BLACK, marginBottom: 6 }}>Generating AI-optimised copy</div>
            <div style={{ fontSize: 13, color: MID, marginBottom: 12 }}>Using Anthropic Claude with your brand voice and search intelligence</div>
          </div>
          <div style={{ width: 340, background: "#f3f4f6", borderRadius: 6, overflow: "hidden", height: 6 }}>
            <div style={{ height: "100%", background: G, borderRadius: 6, transition: "width 0.5s ease", width: `${copy.genProgress.total ? (copy.genProgress.done / copy.genProgress.total) * 100 : 0}%` }} />
          </div>
          <div style={{ fontSize: 12, color: LIGHT }}>{copy.genProgress.done} of {copy.genProgress.total} SKUs - {copy.genProgress.current}</div>
        </div>
      )}

      {/* Shopify Push loading screen */}
      {copy.isPushing && (
        <div data-testid="push-loading-screen" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400, gap: 20, animation: "pop 0.3s ease" }}>
          <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#eef6f1", border: "2px solid #c8e0d0", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ width: 32, height: 32, border: `3px solid ${BORDER}`, borderTopColor: G, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: BLACK, marginBottom: 6 }}>Pushing to Shopify</div>
            <div style={{ fontSize: 13, color: MID, marginBottom: 4 }}>Updating product titles, descriptions, meta fields and GMC attributes</div>
            <div style={{ fontSize: 12, color: LIGHT }}>{copy.pushProgress.current && `Current: ${copy.pushProgress.current}`}</div>
          </div>
          <div style={{ width: 340, background: "#f3f4f6", borderRadius: 6, overflow: "hidden", height: 6 }}>
            <div style={{ height: "100%", background: G, borderRadius: 6, transition: "width 0.5s ease", width: `${copy.pushProgress.total ? (copy.pushProgress.done / copy.pushProgress.total) * 100 : 0}%` }} />
          </div>
          <div style={{ fontSize: 12, color: LIGHT }}>{copy.pushProgress.done} of {copy.pushProgress.total} SKUs</div>
        </div>
      )}

      {/* ═══ SIFT LITE — 3-step flow: Connect → Scan/Fix → Done ═══ */}
      {isLite && !isLoading && (
        <>
          {step === 0 && <S01 tone={brand.tone} setTone={brand.setTone} words={brand.words} setWords={brand.setWords} voice={brand.voice} setVoice={brand.setVoice} onNext={() => { setStep(1); setSel(activeProducts.map(p => p.id)); }} analyticsConnected={google.analyticsConnected} setAnalyticsConnected={google.setAnalyticsConnected} setRealCVR={google.setRealCVR} setRealAOV={google.setRealAOV} shopConnected={shop.shopConnected} shopDomain={shop.shopDomain} shopInfo={shop.shopInfo} shopProducts={shop.shopProducts} shopLoading={shop.shopLoading} onDisconnectShop={shop.disconnect} demoMode={demoMode} onStartDemo={() => handleStartDemo("lite")} googleConnected={google.googleConnected} googleInfo={google.googleInfo} setGoogleConnected={google.setGoogleConnected} setGoogleInfo={google.setGoogleInfo} onSeedComplete={(s) => { shop.fetchShopProducts(s); }} liteMode />}
          {step === 1 && <S04 sel={sel} fixes={fixes} setFixes={setFixes} onBack={() => setStep(0)} onNext={() => setStep(2)} products={activeProducts} liteMode />}
          {step === 2 && <S07 appr={sel} fixes={fixes} onReset={reset} analyticsConnected={google.analyticsConnected} setAnalyticsConnected={google.setAnalyticsConnected} setRealCVR={google.setRealCVR} setRealAOV={google.setRealAOV} realCVR={google.realCVR} realAOV={google.realAOV} products={activeProducts} generatedCopy={{}} pushResults={null} demoMode={demoMode} shopConnected={shop.shopConnected} liteMode />}
        </>
      )}

      {/* ═══ SIFT APP — Full 7-step flow ═══ */}
      {!isLite && !isLoading && (
        <>
          {step === 0 && <S01 tone={brand.tone} setTone={brand.setTone} words={brand.words} setWords={brand.setWords} voice={brand.voice} setVoice={brand.setVoice} onNext={() => { brand.save(shop.shopDomain, demoMode); setStep(1); }} analyticsConnected={google.analyticsConnected} setAnalyticsConnected={google.setAnalyticsConnected} setRealCVR={google.setRealCVR} setRealAOV={google.setRealAOV} shopConnected={shop.shopConnected} shopDomain={shop.shopDomain} shopInfo={shop.shopInfo} shopProducts={shop.shopProducts} shopLoading={shop.shopLoading} onDisconnectShop={shop.disconnect} demoMode={demoMode} onStartDemo={() => handleStartDemo("full")} googleConnected={google.googleConnected} googleInfo={google.googleInfo} setGoogleConnected={google.setGoogleConnected} setGoogleInfo={google.setGoogleInfo} onSeedComplete={(s) => { shop.fetchShopProducts(s); }} />}
          {step === 1 && <S02 onBack={() => setStep(0)} onNext={() => setStep(2)} products={activeProducts} googleConnected={google.googleConnected} shopDomain={shop.shopDomain} />}
          {step === 2 && <S03 sel={sel} setSel={setSel} onBack={() => setStep(1)} onNext={() => copy.generate(sel, () => setStep(3))} onSkipGmc={() => setStep(5)} products={activeProducts} />}
          {step === 3 && <S06 sel={sel} fixes={fixes} onNext={() => setStep(4)} products={activeProducts} generatedCopy={copy.generatedCopy} />}
          {step === 4 && <S04 sel={sel} fixes={fixes} setFixes={setFixes} onBack={() => setStep(3)} onNext={() => setStep(5)} products={activeProducts} />}
          {step === 5 && <S05 sel={sel} fixes={fixes} appr={appr} setAppr={setAppr} onBack={() => setStep(4)} onNext={() => copy.push(() => setStep(6))} analyticsConnected={google.analyticsConnected} realCVR={google.realCVR} realAOV={google.realAOV} products={activeProducts} generatedCopy={copy.generatedCopy} />}
          {step === 6 && <S07 appr={appr} fixes={fixes} onReset={reset} analyticsConnected={google.analyticsConnected} setAnalyticsConnected={google.setAnalyticsConnected} setRealCVR={google.setRealCVR} setRealAOV={google.setRealAOV} realCVR={google.realCVR} realAOV={google.realAOV} products={activeProducts} generatedCopy={copy.generatedCopy} pushResults={copy.pushResults} demoMode={demoMode} shopConnected={shop.shopConnected} />}
        </>
      )}
    </Shell>
  );
}
